/**
 * Utilitário de países e documentos de identificação
 * Centraliza toda a lógica de formatação, validação e metadados por país
 */

export const COUNTRIES = [
  { code: 'BR', name: 'Brasil',     flag: '🇧🇷', currency: 'BRL', locale: 'pt-BR' },
  { code: 'US', name: 'EUA',        flag: '🇺🇸', currency: 'USD', locale: 'en-US' },
  { code: 'PT', name: 'Portugal',   flag: '🇵🇹', currency: 'EUR', locale: 'pt-PT' },
  { code: 'ES', name: 'Espanha',    flag: '🇪🇸', currency: 'EUR', locale: 'es-ES' },
  { code: 'IT', name: 'Itália',     flag: '🇮🇹', currency: 'EUR', locale: 'it-IT' },
  { code: 'FR', name: 'França',     flag: '🇫🇷', currency: 'EUR', locale: 'fr-FR' },
  { code: 'DE', name: 'Alemanha',   flag: '🇩🇪', currency: 'EUR', locale: 'de-DE' },
  { code: 'AR', name: 'Argentina',  flag: '🇦🇷', currency: 'ARS', locale: 'es-AR' },
  { code: 'MX', name: 'México',     flag: '🇲🇽', currency: 'MXN', locale: 'es-MX' },
  { code: 'CO', name: 'Colômbia',   flag: '🇨🇴', currency: 'COP', locale: 'es-CO' },
  { code: 'OTHER', name: 'Outro',   flag: '🌍', currency: 'USD', locale: 'en-US' },
];

/**
 * Configuração de documentos por país e tipo de pessoa
 * company = pessoa jurídica | individual = pessoa física
 */
export const DOCUMENT_CONFIG = {
  BR: {
    company: {
      type: 'cnpj',
      label: 'CNPJ',
      placeholder: '00.000.000/0000-00',
      mask: (v) => {
        const n = v.replace(/\D/g, '').slice(0, 14);
        return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      },
      validate: validateCNPJ,
      required: true,
    },
    individual: {
      type: 'cpf',
      label: 'CPF',
      placeholder: '000.000.000-00',
      mask: (v) => {
        const n = v.replace(/\D/g, '').slice(0, 11);
        return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      },
      validate: validateCPF,
      required: true,
    },
  },
  PT: {
    company:    { type: 'nif', label: 'NIF', placeholder: '000000000', mask: nDigits(9), validate: validateNIF, required: true },
    individual: { type: 'nif', label: 'NIF', placeholder: '000000000', mask: nDigits(9), validate: validateNIF, required: true },
  },
  ES: {
    company:    { type: 'cif', label: 'CIF', placeholder: 'A00000000', mask: (v) => v.toUpperCase().slice(0, 9), validate: validateCIF, required: true },
    individual: { type: 'nie_nif', label: 'NIF/NIE', placeholder: '00000000A', mask: (v) => v.toUpperCase().slice(0, 9), validate: () => true, required: false },
  },
  IT: {
    company:    { type: 'piva', label: 'P.IVA', placeholder: '00000000000', mask: nDigits(11), validate: (v) => v.replace(/\D/g,'').length === 11, required: true },
    individual: { type: 'cf', label: 'Codice Fiscale', placeholder: 'RSSMRA80A01H501U', mask: (v) => v.toUpperCase().slice(0, 16), validate: (v) => v.replace(/\s/g,'').length === 16, required: false },
  },
  FR: {
    company:    { type: 'siret', label: 'SIRET', placeholder: '000 000 000 00000', mask: (v) => { const n = v.replace(/\D/g,'').slice(0,14); return n.replace(/(\d{3})(\d{3})(\d{3})(\d{5})/,'$1 $2 $3 $4'); }, validate: (v) => v.replace(/\D/g,'').length === 14, required: true },
    individual: { type: 'siret', label: 'SIRET', placeholder: '000 000 000 00000', mask: (v) => { const n = v.replace(/\D/g,'').slice(0,14); return n.replace(/(\d{3})(\d{3})(\d{3})(\d{5})/,'$1 $2 $3 $4'); }, validate: (v) => v.replace(/\D/g,'').length === 14, required: false },
  },
  DE: {
    company:    { type: 'steuernummer', label: 'Steuernummer', placeholder: '00/000/00000', mask: (v) => v.slice(0, 13), validate: (v) => v.replace(/\D/g,'').length >= 10, required: true },
    individual: { type: 'steuernummer', label: 'Steuernummer', placeholder: '00/000/00000', mask: (v) => v.slice(0, 13), validate: (v) => v.replace(/\D/g,'').length >= 10, required: false },
  },
  AR: {
    company:    { type: 'cuit', label: 'CUIT', placeholder: '00-00000000-0', mask: (v) => { const n = v.replace(/\D/g,'').slice(0,11); return n.replace(/(\d{2})(\d{8})(\d{1})/,'$1-$2-$3'); }, validate: (v) => v.replace(/\D/g,'').length === 11, required: true },
    individual: { type: 'cuil', label: 'CUIL', placeholder: '00-00000000-0', mask: (v) => { const n = v.replace(/\D/g,'').slice(0,11); return n.replace(/(\d{2})(\d{8})(\d{1})/,'$1-$2-$3'); }, validate: (v) => v.replace(/\D/g,'').length === 11, required: true },
  },
  MX: {
    company:    { type: 'rfc', label: 'RFC', placeholder: 'AAA000000AAA', mask: (v) => v.toUpperCase().slice(0, 12), validate: (v) => v.replace(/\s/g,'').length >= 12, required: true },
    individual: { type: 'rfc', label: 'RFC', placeholder: 'AAAA000000AAA', mask: (v) => v.toUpperCase().slice(0, 13), validate: (v) => v.replace(/\s/g,'').length >= 13, required: false },
  },
  US: {
    company:    { type: 'ein', label: 'EIN', placeholder: '00-0000000', mask: (v) => { const n = v.replace(/\D/g,'').slice(0,9); return n.replace(/(\d{2})(\d{7})/,'$1-$2'); }, validate: () => true, required: false },
    individual: { type: 'ssn', label: 'SSN', placeholder: '000-00-0000', mask: (v) => { const n = v.replace(/\D/g,'').slice(0,9); return n.replace(/(\d{3})(\d{2})(\d{4})/,'$1-$2-$3'); }, validate: () => true, required: false },
  },
  CO: {
    company:    { type: 'nit', label: 'NIT', placeholder: '000.000.000-0', mask: (v) => { const n = v.replace(/\D/g,'').slice(0,10); return n.replace(/(\d{9})(\d{1})/,'$1-$2'); }, validate: (v) => v.replace(/\D/g,'').length >= 9, required: true },
    individual: { type: 'cedula', label: 'Cédula', placeholder: '0000000000', mask: nDigits(10), validate: (v) => v.replace(/\D/g,'').length >= 6, required: false },
  },
  OTHER: {
    company:    { type: 'tax_id', label: 'Tax ID', placeholder: '', mask: (v) => v, validate: () => true, required: false },
    individual: { type: 'tax_id', label: 'ID Document', placeholder: '', mask: (v) => v, validate: () => true, required: false },
  },
};

/** Retorna config do documento para país + tipo de pessoa */
export function getDocumentConfig(countryCode, personType = 'company') {
  return DOCUMENT_CONFIG[countryCode]?.[personType]
    || DOCUMENT_CONFIG.OTHER[personType];
}

/** Retorna moeda padrão do país */
export function getCountryCurrency(countryCode) {
  return COUNTRIES.find(c => c.code === countryCode)?.currency || 'USD';
}

/** Retorna locale padrão do país */
export function getCountryLocale(countryCode) {
  return COUNTRIES.find(c => c.code === countryCode)?.locale || 'en-US';
}

// ── Helpers internos ──────────────────────────────────────────────────────────

function nDigits(n) {
  return (v) => v.replace(/\D/g, '').slice(0, n);
}

// ── Validadores ───────────────────────────────────────────────────────────────

export function validateCPF(cpf) {
  const n = cpf.replace(/\D/g, '');
  if (n.length !== 11 || /^(\d)\1+$/.test(n)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(n[i]) * (10 - i);
  let d = (sum * 10) % 11;
  if (d === 10 || d === 11) d = 0;
  if (d !== parseInt(n[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(n[i]) * (11 - i);
  d = (sum * 10) % 11;
  if (d === 10 || d === 11) d = 0;
  return d === parseInt(n[10]);
}

export function validateCNPJ(cnpj) {
  const n = cnpj.replace(/\D/g, '');
  if (n.length !== 14 || /^(\d)\1+$/.test(n)) return false;
  let sum = 0, pos = 5;
  for (let i = 0; i < 12; i++) { sum += parseInt(n[i]) * pos--; if (pos < 2) pos = 9; }
  let d = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (d !== parseInt(n[12])) return false;
  sum = 0; pos = 6;
  for (let i = 0; i < 13; i++) { sum += parseInt(n[i]) * pos--; if (pos < 2) pos = 9; }
  d = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  return d === parseInt(n[13]);
}

export function validateNIF(nif) {
  const n = nif.replace(/\D/g, '');
  if (n.length !== 9) return false;
  const weights = [9,8,7,6,5,4,3,2];
  const sum = weights.reduce((acc, w, i) => acc + w * parseInt(n[i]), 0);
  const check = 11 - (sum % 11);
  return check >= 10 ? parseInt(n[8]) === 0 : parseInt(n[8]) === check;
}

export function validateCIF(cif) {
  // Validação básica de formato CIF espanhol
  return /^[ABCDEFGHJKLMNPQRSUVW]\d{7}[0-9A-J]$/i.test(cif.trim());
}

/** Valida documento de acordo com país e tipo */
export function validateDocument(value, countryCode, personType) {
  if (!value) return true; // vazio = válido (campo pode ser opcional)
  const config = getDocumentConfig(countryCode, personType);
  return config.validate(value);
}
